import React from 'react';
import { BookingResponseModel } from '../types/BookingModels';
import { NoBookings } from '../pages/new/components/bookings/NoBookings';
import { BookingItemNew } from '../pages/new/components/bookings/BookingItemNew';
import { Space } from '@mantine/core';

const MIN_GROUP_GAP_MS = 15 * 60 * 1000;

const isValidBookingDate = (value: string) => {
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const getBookingStartTime = (booking: BookingResponseModel) =>
  new Date(booking.startIso).getTime();

const getBookingEndTime = (booking: BookingResponseModel) =>
  new Date(booking.endIso).getTime();

const getCalendarDayKey = (booking: BookingResponseModel) =>
  [
    new Date(booking.startIso).getFullYear(),
    String(new Date(booking.startIso).getMonth() + 1).padStart(2, '0'),
    String(new Date(booking.startIso).getDate()).padStart(2, '0'),
  ].join('-');

const getBookingSignature = (booking: BookingResponseModel) =>
  [
    booking.startIso,
    booking.endIso,
    booking.professionalId,
    booking.userId,
    booking.accepted,
    booking.rejected,
    booking.isCancelled,
  ].join('|');

const hasGroupingFieldChanged = (
  previousBooking: BookingResponseModel,
  nextBooking: BookingResponseModel
) =>
  previousBooking.professionalId !== nextBooking.professionalId ||
  previousBooking.userId !== nextBooking.userId ||
  previousBooking.accepted !== nextBooking.accepted ||
  previousBooking.rejected !== nextBooking.rejected ||
  previousBooking.isCancelled !== nextBooking.isCancelled ||
  previousBooking.paymentStatus !== nextBooking.paymentStatus;

const shouldStartNewGroup = (
  previousBooking: BookingResponseModel,
  nextBooking: BookingResponseModel,
  isPastBooking: boolean
) => {
  if (getCalendarDayKey(previousBooking) !== getCalendarDayKey(nextBooking)) {
    return true;
  }

  if (hasGroupingFieldChanged(previousBooking, nextBooking)) {
    return true;
  }

  const bookingGap =
    getBookingStartTime(nextBooking) - getBookingEndTime(previousBooking);
  if (bookingGap >= MIN_GROUP_GAP_MS) {
    return true;
  }

  if (
    !isPastBooking &&
    getBookingSignature(previousBooking) === getBookingSignature(nextBooking)
  ) {
    return true;
  }

  return false;
};

const sortBookingsByStartTime = (bookings: BookingResponseModel[]) =>
  [...bookings].sort(
    (a, b) =>
      getBookingStartTime(a) - getBookingStartTime(b) ||
      getBookingEndTime(a) - getBookingEndTime(b) ||
      a.id - b.id
  );

const dedupePastBookings = (bookings: BookingResponseModel[]) => {
  const seen = new Set<string>();

  return bookings.filter((booking) => {
    const signature = getBookingSignature(booking);
    if (seen.has(signature)) {
      return false;
    }

    seen.add(signature);
    return true;
  });
};

const sortGroupsByStartTime = (
  groups: BookingResponseModel[][],
  isPastBooking: boolean
) =>
  [...groups].sort((a, b) => {
    const startTimeDiff = getBookingStartTime(a[0]) - getBookingStartTime(b[0]);
    return isPastBooking ? -startTimeDiff : startTimeDiff;
  });

interface BookingListProps {
  isPastBooking: boolean;
  bookings: BookingResponseModel[];
  refreshBookings: () => void;
  profile: any;
}

const BookingList: React.FC<BookingListProps> = ({
  bookings,
  profile,
  isPastBooking,
  refreshBookings,
}) => {
  if (bookings.length === 0) {
    return <NoBookings isPastBooking={isPastBooking} />;
  }

  const groupedBookings = groupBookings(bookings, isPastBooking);

  return (
    <>
      {groupedBookings.map((group) => (
        <React.Fragment key={group[0].id}>
          <BookingItemNew
            booking={group[0]}
            profile={profile}
            refreshBookings={refreshBookings}
            groupedBookings={group}
          />
          <Space h={10} />
        </React.Fragment>
      ))}
    </>
  );
};

export const groupBookings = (
  bookings: BookingResponseModel[],
  isPastBooking: boolean = false
) => {
  const validBookings = sortBookingsByStartTime(
    bookings.filter(
      (booking) =>
        isValidBookingDate(booking.startIso) && isValidBookingDate(booking.endIso)
    )
  );

  const filteredBookings = isPastBooking
    ? dedupePastBookings(validBookings)
    : validBookings;

  const groupedBookings = filteredBookings.reduce<BookingResponseModel[][]>(
    (groups, booking) => {
      const currentGroup = groups[groups.length - 1];

      if (!currentGroup) {
        groups.push([booking]);
        return groups;
      }

      const previousBooking = currentGroup[currentGroup.length - 1];
      if (shouldStartNewGroup(previousBooking, booking, isPastBooking)) {
        groups.push([booking]);
      } else {
        currentGroup.push(booking);
      }

      return groups;
    },
    []
  );

  return sortGroupsByStartTime(groupedBookings, isPastBooking);
};

export default BookingList;
